import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "../api";

const MESSENGER_OPTIONS = ["Телеграм", "СМС", "Макс"];

// Показывается по клику на «Сохранить» в сканере — мессенджер/ссылка/категория записи больше не
// вводятся прямо в панели параметров скана (см. ScanParamsPanel.jsx), а подтверждаются здесь, в
// момент фактического сохранения, и только отсюда уходят в пуш в YClients.
export default function SaveDetailsModal({ scanParams, patient, onClose, onSubmit, saving, error, loadPreview, submitLabel = "Сохранить обследование" }) {
  const [step, setStep] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState("");
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [requestPreview] = useState(() => loadPreview);
  const [alias, setAlias] = useState(patient?.alias || patient?.name || "");
  // Мессенджер и ссылка — поля пациента: у нового скана их ещё нет, поэтому подставляем то, что
  // уже записано у пациента. Раньше окно открывалось с «— Не указано», и повторное обследование
  // молча стирало мессенджер постоянного пациента (Уточкина, 27.09–11.10.2026, 7 пациентов).
  const patientMessenger = patient?.messenger || "";
  const patientMessengerLink = patient?.messengerLink || "";
  const [messenger, setMessenger] = useState(scanParams.messenger || patientMessenger);
  const [messengerLink, setMessengerLink] = useState(scanParams.messengerLink || patientMessengerLink);
  const [recordLabelIds, setRecordLabelIds] = useState(scanParams.recordLabelIds || []);
  const [recordLabels, setRecordLabels] = useState([]);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    api.getYclientsRecordLabels().then(r => setRecordLabels(r?.labels || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    requestPreview().then(result => {
      if (!cancelled) { setPreview(result); setPreviewLoading(false); }
    }).catch(err => {
      if (!cancelled) { setPreviewError(err.response?.data?.error || err.message || "Не удалось подготовить изображения отчёта"); setPreviewLoading(false); }
    });
    return () => { cancelled = true; };
  }, [requestPreview, previewAttempt]);

  const toggleLabel = (id) => {
    setRecordLabelIds(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  // Псевдоним — отдельное поле пациента: печатается в отчёте вместо имени. Даже если сотрудник
  // скопировал имя из карточки без изменений, сохраняем его явно — иначе при следующем входе в
  // сканер программа снова сочтёт обязательный псевдоним незаполненным.
  const handleSubmit = () => {
    const value = alias.trim();
    if (patient && !value) { setValidationError("Введите псевдоним для отчёта"); return; }
    setValidationError("");
    const patientAlias = patient ? value : undefined;
    // Пустое значение стирает записанное у пациента только при явной очистке (см. backend
    // applyPatientContact): иначе пустое поле означает «не трогать».
    onSubmit({
      messenger, messengerLink, recordLabelIds, patientAlias,
      clearMessenger: !messenger && Boolean(patientMessenger),
      clearMessengerLink: !messengerLink.trim() && Boolean(patientMessengerLink),
    }, preview);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[250] flex items-center justify-center p-4"
      onMouseDown={e => e.target === e.currentTarget && !saving && onClose()}
    >
      <div role="dialog" aria-modal="true" aria-labelledby="save-exam-title" className="bg-white rounded-xl shadow-2xl w-full max-w-[680px] overflow-hidden flex flex-col" style={{ maxHeight: "88vh" }}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2 flex-shrink-0">
          <span id="save-exam-title" className="text-sm font-semibold text-slate-900">Сохранение обследования</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} disabled={saving}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {step === 0 && <div className="overflow-y-auto flex-1 px-5 py-3">
          <p className="text-xs text-slate-500 mb-3">Так снимки будут выглядеть в отчёте.</p>
          {previewLoading && <div role="status" className="py-12 text-center text-slate-500">Готовим изображения отчёта...</div>}
          {previewError && <div role="alert" className="rounded-lg bg-red-50 p-3 text-xs text-red-700">
            {previewError}<Button variant="outline" className="ml-3" onClick={() => { setPreviewError(""); setPreviewLoading(true); setPreviewAttempt(value => value + 1); }}>Повторить</Button>
          </div>}
          {preview && <>
            {(!preview.leftOriginal || !preview.rightOriginal || !preview.leftHeat || !preview.rightHeat) && <div role="alert" className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {[!preview.leftOriginal && "Не выделена левая стопа.", !preview.rightOriginal && "Не выделена правая стопа."].filter(Boolean).join(" ") || "Не удалось подготовить часть изображений."} Проверьте области стоп на снимке.
            </div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[{ side: "left", label: "Левая стопа", color: "blue" }, { side: "right", label: "Правая стопа", color: "emerald" }].map(({ side, label, color }) => <section key={side}>
                <h3 className={`text-xs font-medium pb-2 ${color === "blue" ? "text-blue-600" : "text-emerald-600"}`}>{label}</h3>
                <div className="grid grid-cols-2 gap-2">
                  {[{ suffix: "Original", label: "Обычный скан" }, { suffix: "Heat", label: "Тепловая карта" }].map(({ suffix, label: imageLabel }) => <figure key={suffix}>
                    <figcaption className="text-[10px] text-slate-400 mb-1">{imageLabel}</figcaption>
                    {preview[side + suffix] ? <img src={preview[side + suffix]} alt={`${label}: ${imageLabel}`} className="save-preview-image w-full object-contain rounded-md bg-slate-50/50" /> : <div className="save-preview-image rounded-md bg-slate-50 flex items-center justify-center text-center p-2 text-xs text-slate-400">Нет изображения</div>}
                  </figure>)}
                </div>
              </section>)}
            </div>
          </>}
        </div>}

        {step === 1 && <div className="overflow-y-auto flex-1 px-5 py-3 flex flex-col gap-3">
          {patient && (
            <div>
              <label htmlFor="save-patient-alias" className="block text-xs text-slate-600 mb-1">ФИО для отчёта (псевдоним)</label>
              <Input
                id="save-patient-alias"
                value={alias}
                onChange={e => setAlias(e.target.value)}
                placeholder={patient.name}
                className="h-8 text-xs"
              />
            </div>
          )}

          <div>
            <p className="text-xs text-slate-600 mb-1.5">Мессенджер</p>
            <div role="group" aria-label="Мессенджер" className="flex flex-wrap gap-1">
              <button type="button" aria-pressed={!messenger}
                className={`px-2.5 py-1.5 rounded-md text-xs transition-colors ${!messenger ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-500 hover:bg-slate-50"}`}
                onClick={() => setMessenger("")}
              >
                <span>— Не указано</span>
              </button>
              {MESSENGER_OPTIONS.map(opt => (
                <button type="button" aria-pressed={messenger === opt}
                  key={opt}
                  className={`px-2.5 py-1.5 rounded-md text-xs transition-colors ${messenger === opt ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-500 hover:bg-slate-50"}`}
                  onClick={() => setMessenger(opt)}
                >
                  <span>{opt}</span>
                </button>
              ))}
            </div>
            <Input
              placeholder="Ссылка на мессенджер..."
              value={messengerLink}
              onChange={e => setMessengerLink(e.target.value)}
              className="h-8 text-xs mt-2"
            />
          </div>

          {recordLabels.length > 0 && <div>
            <p className="text-xs text-slate-600 mb-1.5">Категория записи (YClients)</p>
              <div className="flex flex-wrap gap-1">
                {recordLabels.map(l => {
                  const active = recordLabelIds.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => toggleLabel(l.id)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${active ? "bg-blue-50 text-blue-700 border-blue-300" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                    >
                      {l.title}
                    </button>
                  );
                })}
              </div>
          </div>}
        </div>}

        {step === 1 && (validationError || error) && <div role="alert" className="mx-5 mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{validationError || error}</div>}
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-5 py-3 border-t border-slate-100 flex-shrink-0">
          <div className="justify-self-start">
          <Button size="sm" variant="ghost" onClick={step === 0 ? onClose : () => setStep(0)} disabled={saving}>{step === 0 ? "К снимку" : "Назад"}</Button>
          </div>
          <div role="progressbar" aria-label="Сохранение обследования" aria-valuemin={1} aria-valuemax={2} aria-valuenow={step + 1} aria-valuetext={`Шаг ${step + 1} из 2`} className="flex gap-1 w-12 shrink-0">
            {[0, 1].map(index => <span key={index} data-state={index < step ? "complete" : index === step ? "current" : "pending"} className={`h-[3px] flex-1 rounded-full ${index === step ? "bg-blue-500" : index < step ? "bg-blue-300" : "bg-slate-200"}`} />)}
          </div>
          <div className="justify-self-end">
          {step === 0 ? <Button size="sm" onClick={() => setStep(1)} disabled={previewLoading || !preview || Boolean(previewError)}>Далее</Button> : <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving ? "Сохранение..." : submitLabel}
          </Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
